"use client";

import { Atom, Beaker, Box, Cpu, Droplet, Flame, Layers, Shield } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Vpnenterprisedataprotection() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const materials = [{
    name: "AES-256",
    description: "Military-grade encryption standard ensuring your data remains unreadable to unauthorized parties.",
    tensile: "256-bit",
    temp: "Secure",
    Icon: Layers
  }, {
    name: "Kill Switch",
    description: "Instant connection termination if the VPN drops, preventing accidental data exposure online.",
    tensile: "Instant",
    temp: "Active",
    Icon: Droplet
  }, {
    name: "No-Logs",
    description: "Strict policy ensuring your browsing history and metadata are never recorded or stored.",
    tensile: "Zero",
    temp: "Private",
    Icon: Beaker
  }, {
    name: "WireGuard",
    description: "Modern, high-speed tunneling protocol providing superior performance and robust security.",
    tensile: "Fast",
    temp: "Stable",
    Icon: Box
  }, {
    name: "Multi-Hop",
    description: "Route traffic through multiple servers for an extra layer of anonymity and protection.",
    tensile: "Double",
    temp: "Hidden",
    Icon: Atom
  }, {
    name: "DNS Leak",
    description: "Advanced protection preventing your ISP from tracking your DNS requests and activity.",
    tensile: "Shield",
    temp: "Safe",
    Icon: Shield
  }, {
    name: "Split Tunnel",
    description: "Selectively route specific app traffic through the VPN while keeping others on local.",
    tensile: "Custom",
    temp: "Flex",
    Icon: Cpu
  }, {
    name: "Obfuscation",
    description: "Mask VPN traffic to appear as regular HTTPS, bypassing strict network restrictions.",
    tensile: "Stealth",
    temp: "Open",
    Icon: Flame
  }];
  return <section data-section-id="2254" id="vpnenterprisedataprotection" className="bg-background text-foreground py-20 md:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-16 md:mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="md:col-span-6">
            <p className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase mb-6" style={{
            color: "var(--primary)"
          }}>
              Security Suite
            </p>
            <h2 className="font-serif font-default text-4xl md:text-5xl lg:text-[56px] leading-[1.05] tracking-tight text-foreground">
              Enterprise-grade data protection.
            </h2>
          </div>
          <div className="md:col-span-6 md:pt-4">
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-xl">
              From advanced encryption protocols to strict no-logs policies, our security suite
              provides the robust, reliable, and private infrastructure your business
              operations demand.
            </p>
          </div>
        </div>

        {}
        <div ref={gridRef} className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-700 ease-out ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {materials.map((material, i) => {
          const Icon = material.Icon;
          return <div data-index={i} key={material.name} className="group relative rounded-2xl border border-border bg-card p-7 md:p-8 transition-all duration-300 ease-out hover:-translate-y-1 shadow-sm hover:shadow-lg" style={{
            backgroundColor: "var(--card)"
          }}>
                {}
                <div className="mb-7 inline-flex h-[60px] w-[60px] items-center justify-center rounded-lg transition-colors duration-300" style={{
              backgroundColor: "var(--muted)"
            }}>
                  <Icon className="h-6 w-6 transition-colors duration-300 group-hover:text-primary-foreground" strokeWidth={1.25} style={{
                color: "var(--primary)"
              }} />
                  <span className="absolute pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300 h-[60px] w-[60px] rounded-lg -z-0" aria-hidden="true" />
                </div>

                {}
                <style>{`
                  .group:hover [data-badge-fill="${i}"] {
                    background-color: var(--primary);
                  }
                `}</style>

                {}
                <h3 className="font-serif font-default text-[28px] leading-tight text-card-foreground mb-3">
                  {material.name}
                </h3>

                {}
                <p className="text-sm leading-relaxed text-muted-foreground mb-8">
                  {material.description}
                </p>

                {}
                <div className="pt-5 border-t border-border">
                  <p className="font-mono text-xs tracking-wider uppercase" style={{
                color: "var(--muted-foreground)"
              }}>
                    <span style={{
                  color: "var(--primary)"
                }}>LEVEL:</span>{" "}
                    <span className="text-foreground">{material.tensile}</span>
                    <span className="mx-2 opacity-50">·</span>
                    <span style={{
                  color: "var(--primary)"
                }}>STATUS:</span>{" "}
                    <span className="text-foreground">{material.temp}</span>
                  </p>
                </div>
              </div>;
        })}
        </div>
      </div>

      {}
      <style>{`
        #vpnenterprisedataprotection .group:hover .material-badge-icon {
          color: var(--primary-foreground);
        }
      `}</style>
    </section>;
}